import isUndefined from 'lodash/isUndefined';
import omitBy from 'lodash/omitBy';
import { action, makeObservable, observable, reaction, untracked } from 'mobx';

import i18n from '../../i18n/config';
import { getFirebaseAuthToken } from '../../services/firebase';
import { ldClient } from '../../services/launchDarkly';
import { devWarn } from '../../utils/log';
import { trackJackpotWidgetOpen } from '../jackpot/jackpotAnalytics';
import { applyJackpotFlagOverride } from '../jackpot/resolveJackpotFlag';
import type { JackpotCoinType } from '../jackpot/types';
import type { RootStore } from '../RootStore';
import { EnumOpenDialog } from '../ui/types';

export class GameUiStore {
  private readonly rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeObservable(this);

    window.addEventListener('message', this.onMessageEvent);

    // The Sound switch rides the launch URL, which cannot be changed on a document that is already
    // open — a new src reloads the game mid-spin. So a flip is delivered as a message instead, to
    // every game-ui document at once.
    //
    // Broadcast rather than per-store wiring: slots, the warm metagames frame and each dialog
    // (bingo, challenges, sports, jackpot, cash-offer…) are separate iframes owned by separate
    // stores, and every one of them that was missed would keep playing. Finding them in the DOM
    // means a new surface is covered the day it is added.
    reaction(
      () => this.rootStore.uiStore.disabledSounds,
      (disabledSounds) => this.broadcastSoundEnabled(!disabledSounds),
    );
  }

  private broadcastSoundEnabled(enabled: boolean): void {
    const gameUiOrigin = import.meta.env.VITE_GAME_UI_URL;

    document.querySelectorAll('iframe').forEach((iframe) => {
      // Only our own documents. Compared as a parsed origin rather than a string prefix, which would
      // also accept a lookalike host (`https://game.example.com.somewhere-else.test`). The explicit
      // target origin below is what actually guarantees delivery, so this is about not asking.
      if (!iframe.src || new URL(iframe.src, window.location.href).origin !== new URL(gameUiOrigin).origin) {
        return;
      }

      try {
        iframe.contentWindow?.postMessage({ type: 'setSoundEnabled', payload: { enabled } }, gameUiOrigin);
      } catch (error) {
        devWarn('GameUiStore: posting the Sound switch to a game-ui frame failed', error);
      }
    });
  }

  @observable
  public newGameUiAuthEnabled: boolean = true;

  @action
  public setNewGameUiAuthEnabled(flag: boolean) {
    this.newGameUiAuthEnabled = flag;
  }

  public getGameUiURL = (path: string, params: Record<string, string | number | boolean | undefined> = {}) => {
    const allParams = {
      ...params,
      platform: 'WEB',
      lang: i18n.language,
      version: this.newGameUiAuthEnabled ? '3' : '2',
      // The app-wide Sound switch, seeding game-ui's app context. game-ui reads `soundEnabled` and
      // treats only the literal 'false' as off — an earlier `disabledSounds` param was never read
      // there, so the toggle did nothing. Sent from here so EVERY game-ui surface gets it, not just
      // slots. Read untracked: this runs inside GameStore's `gameUiUrl` computed, and observing the
      // toggle there would change the live iframe's src and reload the game mid-session. Runtime
      // changes travel as the `setSoundEnabled` message instead (GameStore, CrownRipsStore).
      soundEnabled: String(!untracked(() => this.rootStore.uiStore.disabledSounds)),
    };
    return `${import.meta.env.VITE_GAME_UI_URL}/${path}?${new URLSearchParams(omitBy(allParams, isUndefined)).toString()}`;
  };

  public openGameUiDialog = (path: string, params: Record<string, string | number | boolean | undefined> = {}) => {
    const { dataTestId } = params;
    const url = this.getGameUiURL(path, params);
    this.rootStore.uiStore.setOpenDialog({ dialog: EnumOpenDialog.isOpenGameUiDialog, options: { url, dataTestId } });
  };

  public openCashOfferDialog = () => {
    const offer = this.rootStore.walletStore.balance.cashOffer;
    this.openGameUiDialog('cash-offer', {
      dataTestId: 'cash-offer-dialog-iframe',
      ...(offer && {
        cashOfferPrice: offer.price,
        cashOfferSc: offer.scAmount,
        cashOfferGc: offer.gcAmount,
        cashOfferGcPerDollar: offer.gcPerDollar,
      }),
    });
  };

  /**
   * game-ui owns the jackpot popup itself (FE_DESIGN_SPEC §1) — this only opens it. Mirrors
   * openCashOfferDialog: the context is read off the store rather than passed in, so every entry
   * point (category row, promo card, nav icon, crown badges) opens the same dialog the same way.
   *
   * No longer the only route into opt-in: the promo card's `OPT IN` CTA now posts participation
   * itself (`JackpotStore.optIn`). Everything else still belongs here — opting OUT, PLAY NOW, the
   * ladder, the eligible games and How-It-Works — and the popup remains the surface a failed widget
   * opt-in falls back to.
   */
  public openJackpotDialog = ({
    trigger = 'click',
    source = 'card',
  }: { trigger?: 'click' | 'auto'; source?: 'cta' | 'card' } = {}) => {
    const { loadedCoinType, eligibilityForSelectedCoin, isOptedIn, topLevelAmount } = this.rootStore.jackpotStore;

    /*
     * The `lobby_widget` click events live here, not in the widgets. Four surfaces open this dialog and
     * the wide widget's CTA calls `stopPropagation`, so a handler on the rail wrapper alone misses the
     * tap players actually make. Tracking the open covers every current entry point and every future one.
     *
     * `auto` is the jump-paced auto-open (`JackpotStore.maybeAutoOpenPopup`): a real open, but nobody
     * clicked, so reporting it as a click would inflate lobby CTR. game-ui still reports the resulting
     * `popup` impression either way.
     */
    if (trigger === 'click') {
      // `loadedCoinType` is the group actually being opened; the lobby's own toggle can be a frame ahead.
      trackJackpotWidgetOpen(loadedCoinType ?? null, isOptedIn, topLevelAmount, source);
    }

    /*
     * The page the player is actually on. Every Crown Jackpot event carries `page_url`, and game-ui
     * runs at its own `/jackpot` URL — without this, every event it sends would report the jackpot
     * surface instead of the lobby. On the WARM path it matters more, not less: that document's own
     * location is game-ui's root, so there is no `/jackpot` in the URL bar to fall back to.
     *
     * Jackpots are per coin group, so the popup is opened for one coin — never a two-tab dialog.
     */
    const params = {
      pageUrl: window.location.href,
      ...(loadedCoinType && { coinType: loadedCoinType }),
      ...(eligibilityForSelectedCoin?.jackpotId && { jackpotId: eligibilityForSelectedCoin.jackpotId }),
    };

    /*
     * WARM FIRST. The popup used to be an iframe created on tap, so every open paid a document
     * fetch, a bundle parse and an app boot — measured p50 766ms, p90 3.7s across 33.7k opens/day,
     * of which the two data calls are ~21ms. The boot is the cost, and the warm frame has already
     * paid it: `launchGameUIPath` is the same message mobile has used for every game-ui surface for
     * as long as it has had one WebView.
     *
     * `launch` returning false is a supported, silent path — the frame is not ready, another surface
     * is live, or its window is detached — and the dialog below is the fallback. Both render the
     * same loader, so the player cannot tell which one ran.
     */
    const warmLaunched = this.rootStore.metagamesWarmStore.launch('jackpot', 'launchGameUIPath', {
      path: 'jackpot',
      search: new URLSearchParams(params).toString(),
    });

    if (!warmLaunched) {
      this.openGameUiDialog('jackpot', { dataTestId: 'jackpot-dialog-iframe', ...params });
    }

    this.refreshJackpotOnDialogClose();
  };

  /**
   * Opens game-ui for a WIN and nothing else — `?winOnly=1`.
   *
   * Not `openJackpotDialog`: that route draws the lobby popup, and a player who has just won money is
   * owed their prize, not an information screen they did not ask for. In this mode the route renders
   * nothing and the ceremony (`JackpotWinFlowHost`, mounted app-level in game-ui) is the only thing on
   * screen; it reports no popup impression, makes no overview request, and asks us to close the dialog
   * when the win is claimed.
   *
   * No click event either — nobody tapped anything. The lobby found the win by polling.
   */
  public openJackpotWinDialog = () => {
    const { loadedCoinType } = this.rootStore.jackpotStore;

    this.openGameUiDialog('jackpot', {
      dataTestId: 'jackpot-win-dialog-iframe',
      winOnly: '1',
      // Every Crown Jackpot event carries `page_url`, and game-ui runs at its own URL.
      pageUrl: window.location.href,
      ...(loadedCoinType && { coinType: loadedCoinType }),
    });

    // The claim moves the wallet and can change participation state; refresh on close like the popup.
    this.refreshJackpotOnDialogClose();
  };

  // Disposer for the pending close-reaction below. Reopening the popup replaces it rather than
  // stacking a second reaction (and a second refetch) on top of the first.
  /**
   * Game the jackpot popup's `PLAY NOW` asked us to launch, or `null`.
   *
   * Parked here rather than navigated to directly: a store has no router. `useJackpotGameLaunch`
   * consumes it, navigates, and closes whichever surface the popup is showing in.
   *
   * TWO surfaces can be showing it: the cold `GameUiDialog` and the warm `MetagamesWarmFrame`. Both
   * mount that hook, because `openJackpotDialog` picks between them at open time and neither knows in
   * advance which one it will be. This used to say "`GameUiDialog` is mounted whenever this can be
   * set" — that stopped being true the moment the popup could open warm, and PLAY NOW went dead:
   * the message still arrived and the game was still parked here, but no mounted component read it.
   */
  @observable
  public pendingJackpotGame: { gameId: number; coinType: JackpotCoinType } | null = null;

  @action
  private setPendingJackpotGame = (game: { gameId: number; coinType: JackpotCoinType }) => {
    this.pendingJackpotGame = game;
  };

  /**
   * Take the pending launch and clear it in one step.
   *
   * CONSUMED rather than read-then-cleared because both surfaces can be mounted at once: they would
   * each render with the same value and each navigate. Here the first effect to run gets the game and
   * the second gets `null`.
   */
  @action
  public consumeJackpotGameLaunch = (): { gameId: number; coinType: JackpotCoinType } | null => {
    const pending = this.pendingJackpotGame;

    this.pendingJackpotGame = null;

    return pending;
  };

  private disposeJackpotDialogClose: (() => void) | null = null;

  /**
   * The popup is the single opt-in surface, so a participation change made inside it is invisible to
   * the lobby until the shared store refetches. Refreshing on close updates every jackpot widget
   * together — category row, promo card, nav icon, crown badges. Same one-shot close-reaction the
   * CashOfferStore uses; it also covers the popup being dismissed before its
   * `jackpotParticipationChanged` message got out.
   */
  private refreshJackpotOnDialogClose = () => {
    this.disposeJackpotDialogClose?.();

    /*
     * BOTH surfaces, because the popup now opens on either. The warm path never touches
     * `openDialog`, so watching that alone meant the reaction simply never fired and the lobby kept
     * a stale opt-in state until the 30s poll caught up.
     */
    const dispose = reaction(
      () =>
        this.rootStore.uiStore.openDialog.dialog === EnumOpenDialog.isOpenGameUiDialog ||
        this.rootStore.metagamesWarmStore.activeKey === 'jackpot',
      (isOpen: boolean) => {
        if (!isOpen) {
          dispose();
          this.disposeJackpotDialogClose = null;
          void this.rootStore.jackpotStore.fetchEligibility();
        }
      },
    );

    this.disposeJackpotDialogClose = dispose;
  };

  public closeGameUiDialog = () => {
    this.rootStore.uiStore.setCloseDialog();
  };

  private onMessageEvent = (event: MessageEvent) => {
    const { data, origin, source } = event;

    if (origin !== import.meta.env.VITE_GAME_UI_URL) {
      return;
    }

    switch (data.type) {
      case 'getFlags':
        if (source && ldClient) {
          source.postMessage(
            {
              type: 'getFlags_response',
              payload: {
                // Raw LD values, except that the jackpot dev override is folded in: game-ui gates
                // its whole jackpot surface on `crown_jackpot_splash_feature` and posts `closeGame` when it
                // reads false, so a lobby forced on by the override must hand the iframe the same
                // answer or the popup shuts the moment it opens. No-op in production builds.
                flags: applyJackpotFlagOverride(ldClient.allFlags()),
              },
            },
            { targetOrigin: import.meta.env.VITE_GAME_UI_URL },
          );
        }
        break;
      case 'getIdToken': {
        void getFirebaseAuthToken().then((token) => {
          if (source && token) {
            source.postMessage(
              {
                type: 'getIdToken_response',
                payload: { token },
              },
              { targetOrigin: import.meta.env.VITE_GAME_UI_URL },
            );
          }
        });
        break;
      }
      // Crown Rips parallel launch: the iframe (opened before launch-game finished) asks for its
      // session; CrownRipsStore delivers it — or re-runs launch-game — via this iframe window.
      case 'crownRipsSessionRequest':
        this.rootStore.crownRipsStore.handleSessionRequest(source as Window | null);
        break;
      // game-ui -> host: the jackpot popup changed the player's participation. It is the only
      // surface that posts /jackpot/participation (FE_DESIGN_SPEC §7.4), so this is the lobby's
      // only signal short of the 30s poll — refetch so every jackpot widget updates while the
      // popup is still open, rather than only once it closes.
      case 'jackpotParticipationChanged':
        void this.rootStore.jackpotStore.fetchEligibility();
        break;
      /*
       * game-ui -> host -> game-ui: a jackpot win was claimed, relayed to our OTHER game-ui window.
       *
       * Two of them can hold the same PENDING win at once. `GameIframe` is mounted at the casino
       * shell and only HIDDEN while the player is in the lobby (it keeps its socket that way), so the
       * in-slot banner survives the trip with the win still queued; the jackpot dialog is a separate
       * document that fetches the same win and claims it. Neither can see the other, and the survivor
       * has no reason to refetch — its recovery read runs once per mount. Without this relay the
       * player comes back to the game and is still offered `CLAIM` for money already paid.
       *
       * Not sent back to the window it came from: a document that claimed the win has already
       * dropped it, and `dropClaimedWin` would be a no-op there anyway.
       */
      case 'jackpotWinClaimed': {
        const winId = (data.payload as { winId?: string } | undefined)?.winId;
        const gameIframeWindow = this.rootStore.gameStore.slotsGameUiIframe?.contentWindow;

        if (winId && source !== gameIframeWindow) {
          this.rootStore.gameStore.sendSlotsGameUiMessage({ type: 'jackpotWinClaimed', payload: { winId } });
        }
        break;
      }
      // game-ui -> host: PLAY NOW, with the game centred in the popup's eligible-games carousel.
      // game-ui cannot route to a slot from inside its iframe, so the launch is ours; the dialog is
      // torn down by whoever handles `pendingJackpotGame`, after navigation, so the player never
      // sees the lobby flash between the two.
      case 'jackpotPlayGame':
        // `data` is the whole envelope — the game rides in `payload`, as with every other
        // game-ui message that carries one.
        this.setPendingJackpotGame(data.payload as { gameId: number; coinType: JackpotCoinType });
        break;
      // Unknown types fall through deliberately: game-ui hosts several features on this one
      // channel and each posts its own messages, so anything unrecognised is another feature's.
    }
  };
}
